/**
 * Houtwijzer. Welke paddenstoel groeit op welk hout, in drie weergaven:
 *   boom   de klant heeft hout en wil weten wat erop kan
 *   soort  de klant wil een soort en wil weten welk hout hij nodig heeft
 *   tabel  alles in een keer
 *
 * De onderzoeksmatrix komt uit [data-houtwijzer-data] (gegenereerd), de
 * productgegevens live uit [data-houtwijzer-products] (de Shopify-collectie). Een
 * soort verschijnt alleen als er een product van in de collectie staat.
 *
 * Deeplinks: ?boom=wilg, ?soort=shiitake of ?weergave=tabel. De adresbalk loopt
 * mee met wat de klant kiest, zodat een link delen of terugklikken werkt.
 *
 * Boompagina's: staat data-boom op het element (een pagina als
 * /pages/houtwijzer-populier), dan opent de houtwijzer op die boom en gaat een
 * andere boom naar zijn eigen pagina. De adressen komen uit
 * [data-houtwijzer-paginas] in de gegenereerde data.
 */
class SamzwamHoutwijzer extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready) return;
    this.dataset.ready = '1';

    this.el = {
      soon: this.querySelector('[data-soon]'),
      body: this.querySelector('[data-body]'),
      tool: this.querySelector('[data-tool]'),
    };

    try {
      this.data = JSON.parse(this.querySelector('[data-houtwijzer-data]').textContent);
      this.producten = JSON.parse(this.querySelector('[data-houtwijzer-products]').textContent);
      const alle = JSON.parse(this.querySelector('[data-houtwijzer-strings]').textContent);
      this.taal = (this.dataset.locale || 'nl').slice(0, 2);
      if (!alle[this.taal]) this.taal = 'nl';
      this.s = alle[this.taal];
    } catch (e) {
      // Zonder data valt er niets interactiefs te tonen. De statische lijst blijft
      // dan gewoon staan, dus de klant ziet nog steeds de inhoud.
      if (window.console) console.error('[samzwam-houtwijzer] data niet te lezen', e);
      return;
    }

    if (!this.poortOpen()) return;
    this.start();
  }

  /**
   * Het beheerdersslot, zelfde werking als de stammetjes-zoeker. Liquid heeft al
   * beslist voor beheerders en de thema-editor; hier alleen nog de sleutel in de
   * URL, die in de sessie onthouden wordt.
   */
  poortOpen() {
    if (this.dataset.gate === 'open') return true;
    const sleutel = this.dataset.key || '';
    const opgegeven = new URLSearchParams(location.search).get('voorbeeld');
    let onthouden = null;
    try { onthouden = sessionStorage.getItem('samzwam-houtwijzer-voorbeeld'); } catch (e) { /* prive-venster */ }
    if (sleutel && (opgegeven === sleutel || onthouden === sleutel)) {
      try { sessionStorage.setItem('samzwam-houtwijzer-voorbeeld', sleutel); } catch (e) { /* prive-venster */ }
      if (this.el.soon) this.el.soon.hidden = true;
      if (this.el.body) this.el.body.hidden = false;
      return true;
    }
    return false;
  }

  start() {
    this.bouwIndex();
    if (!this.soorten.length) {
      // Geen enkele soort met een product in de collectie: dan liever de
      // statische lijst laten staan dan een lege houtwijzer tonen.
      return;
    }

    this.querySelectorAll('[data-static]').forEach((n) => { n.hidden = true; });
    this.el.tool.hidden = false;

    const q = new URLSearchParams(location.search);
    this.weergave = ['boom', 'soort', 'tabel'].includes(q.get('weergave'))
      ? q.get('weergave') : (this.dataset.start || 'boom');
    this.boom = this.bomen.find((b) => b.id === q.get('boom')) ? q.get('boom') : null;
    this.soort = this.soortOp[q.get('soort')] ? q.get('soort') : null;
    if (q.get('boom') && this.boom) this.weergave = 'boom';
    if (q.get('soort') && this.soort) this.weergave = 'soort';

    // Boompagina (/pages/houtwijzer-populier): die boom is de pagina zelf.
    // Andere bomen zijn eigen pagina's; de adressen staan per taal in de data.
    this.paginaBoom = this.bomen.find((b) => b.id === this.dataset.boom) ? this.dataset.boom : null;
    try {
      const p = this.querySelector('[data-houtwijzer-paginas]');
      this.paginas = p ? JSON.parse(p.textContent) : {};
    } catch (e) { this.paginas = {}; }
    if (this.paginaBoom && !q.get('boom') && !q.get('soort') && !q.get('weergave')) {
      this.weergave = 'boom';
      this.boom = this.paginaBoom;
    }

    this.teken();
  }

  /** Voegt de onderzoeksmatrix en de live producten samen. */
  bouwIndex() {
    const opHandle = {};
    this.producten.forEach((p) => { opHandle[p.h] = p; });

    // Een foto die bij drie of meer producten terugkomt is een algemene foto
    // (een hoop pluggen), geen foto van de soort. Die zestien keer naast
    // elkaar zegt niets, dus die blijft weg. Shopify zet bij een herupload een
    // UUID achter de bestandsnaam (_f83b7976-39e5-4581-...); dat telt niet mee.
    const stam = (url) => (url || '').split('?')[0].split('/').pop()
      .replace(/\.[a-z0-9]+$/i, '').replace(/_[0-9a-f-]{6,}$/i, '').toLowerCase();
    const telling = {};
    this.producten.forEach((p) => { if (p.i) telling[stam(p.i)] = (telling[stam(p.i)] || 0) + 1; });
    this.producten.forEach((p) => { if (p.i && telling[stam(p.i)] >= 3) p.i = null; });

    this.soortOp = {};
    this.soorten = [];
    this.data.soorten.forEach((s) => {
      const prods = (s.handles || []).map((h) => opHandle[h]).filter(Boolean);
      if (!prods.length) return;
      const soort = Object.assign({}, s, { prods });
      this.soortOp[s.id] = soort;
      this.soorten.push(soort);
    });

    // Alleen bomen met minstens een soort die we verkopen.
    this.bomen = this.data.bomen.filter((b) =>
      (this.data.cellen[b.id] || []).some((c) => this.soortOp[c.s]));

    // Omgekeerde index: per soort, per boom het oordeel.
    this.perSoort = {};
    Object.keys(this.data.cellen).forEach((boom) => {
      (this.data.cellen[boom] || []).forEach((c) => {
        if (!this.soortOp[c.s]) return;
        (this.perSoort[c.s] = this.perSoort[c.s] || []).push(Object.assign({ boom }, c));
      });
    });
  }

  /* ------------------------------------------------------------------------ */
  /* Weergave                                                                  */
  /* ------------------------------------------------------------------------ */

  teken() {
    const t = this.el.tool;
    t.textContent = '';
    t.appendChild(this.tabs());
    t.appendChild(this.legenda());

    const paneel = document.createElement('div');
    paneel.className = 'samzwam-houtwijzer__paneel';
    paneel.id = this.id('paneel');
    paneel.setAttribute('role', 'tabpanel');
    paneel.setAttribute('aria-labelledby', this.id('tab-' + this.weergave));

    if (this.weergave === 'boom') this.paneelBoom(paneel);
    else if (this.weergave === 'soort') this.paneelSoort(paneel);
    else this.paneelTabel(paneel);

    t.appendChild(paneel);

    const status = document.createElement('p');
    status.className = 'visually-hidden';
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    status.textContent = this.statusTekst();
    t.appendChild(status);

    this.werkAdresBij();
  }

  tabs() {
    const rij = document.createElement('div');
    rij.className = 'samzwam-houtwijzer__tabs';
    rij.setAttribute('role', 'tablist');
    [['boom', 'tab_boom'], ['soort', 'tab_soort'], ['tabel', 'tab_tabel']].forEach(([w, sleutel]) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.id = this.id('tab-' + w);
      b.className = 'samzwam-houtwijzer__tab';
      b.setAttribute('role', 'tab');
      b.setAttribute('aria-selected', w === this.weergave ? 'true' : 'false');
      b.setAttribute('aria-controls', this.id('paneel'));
      b.tabIndex = w === this.weergave ? 0 : -1;
      b.textContent = this.s[sleutel];
      b.addEventListener('click', () => this.kiesWeergave(w));
      b.addEventListener('keydown', (e) => this.tabToets(e, w));
      rij.appendChild(b);
    });
    return rij;
  }

  /** Pijltjestoetsen tussen de tabbladen, zoals een tablist hoort te werken. */
  tabToets(e, w) {
    const volgorde = ['boom', 'soort', 'tabel'];
    let i = volgorde.indexOf(w);
    if (e.key === 'ArrowRight') i = (i + 1) % 3;
    else if (e.key === 'ArrowLeft') i = (i + 2) % 3;
    else if (e.key === 'Home') i = 0;
    else if (e.key === 'End') i = 2;
    else return;
    e.preventDefault();
    this.kiesWeergave(volgorde[i]);
    const knop = this.querySelector('#' + this.id('tab-' + volgorde[i]));
    if (knop) knop.focus();
  }

  kiesWeergave(w) {
    if (w === this.weergave) return;
    this.weergave = w;
    this.teken();
  }

  legenda() {
    const d = document.createElement('div');
    d.className = 'samzwam-houtwijzer__legenda';
    const kop = document.createElement('span');
    kop.className = 'samzwam-houtwijzer__legenda-kop';
    kop.textContent = this.s.legenda + ':';
    d.appendChild(kop);
    ['u', 'g', 'm', 'a'].forEach((o) => {
      const item = document.createElement('span');
      item.className = 'samzwam-houtwijzer__legenda-item';
      item.appendChild(this.tekenEl(o));
      item.appendChild(document.createTextNode(' ' + this.s['oordeel_' + o]));
      d.appendChild(item);
    });
    return d;
  }

  /* --- boom -------------------------------------------------------------- */

  paneelBoom(paneel) {
    paneel.appendChild(this.kiezer(
      this.s.kies_boom,
      this.bomen.map((b) => ({ id: b.id, label: b.naam[this.taal] || b.naam.nl })),
      this.boom,
      (id) => this.kiesBoom(id)
    ));

    if (!this.boom) return;
    const boom = this.bomen.find((b) => b.id === this.boom);
    const cellen = (this.data.cellen[this.boom] || []).filter((c) => this.soortOp[c.s]);

    const kop = document.createElement('h3');
    kop.className = 'samzwam-houtwijzer__kop h3';
    kop.textContent = boom.naam[this.taal] || boom.naam.nl;
    paneel.appendChild(kop);

    if (boom.tip) paneel.appendChild(this.tip(boom.tip[this.taal] || boom.tip.nl));

    if (!cellen.length) {
      const p = document.createElement('p');
      p.textContent = this.s.geen;
      paneel.appendChild(p);
      return;
    }

    ['u', 'g', 'm', 'a'].forEach((o) => {
      const groep = cellen.filter((c) => c.o === o);
      if (!groep.length) return;
      const sectie = document.createElement('section');
      sectie.className = 'samzwam-houtwijzer__groep samzwam-houtwijzer__groep--' + o;
      const h = document.createElement('h4');
      h.className = 'samzwam-houtwijzer__groep-kop';
      h.appendChild(this.tekenEl(o));
      h.appendChild(document.createTextNode(' ' + this.s['groep_' + o]));
      sectie.appendChild(h);
      const lijst = document.createElement('ul');
      lijst.className = 'samzwam-houtwijzer__kaarten';
      groep.forEach((c) => lijst.appendChild(this.kaart(this.soortOp[c.s], c, o === 'a')));
      sectie.appendChild(lijst);
      paneel.appendChild(sectie);
    });
  }

  /* --- soort ------------------------------------------------------------- */

  paneelSoort(paneel) {
    paneel.appendChild(this.kiezer(
      this.s.kies_soort,
      this.soorten.map((s) => ({ id: s.id, label: s.naam[this.taal] || s.naam.nl })),
      this.soort,
      (id) => { this.soort = id; this.teken(); }
    ));

    if (!this.soort) return;
    const soort = this.soortOp[this.soort];
    const rijen = (this.perSoort[this.soort] || []).slice();
    const volgorde = { u: 0, g: 1, m: 2, a: 3 };
    rijen.sort((a, b) => volgorde[a.o] - volgorde[b.o]);

    const kop = document.createElement('h3');
    kop.className = 'samzwam-houtwijzer__kop h3';
    kop.textContent = this.s.beste_hout.replace('[soort]', soort.naam[this.taal] || soort.naam.nl);
    paneel.appendChild(kop);

    if (soort.tip) paneel.appendChild(this.tip(soort.tip[this.taal] || soort.tip.nl));

    ['u', 'g', 'm', 'a'].forEach((o) => {
      const groep = rijen.filter((c) => c.o === o);
      if (!groep.length) return;
      const sectie = document.createElement('section');
      sectie.className = 'samzwam-houtwijzer__groep samzwam-houtwijzer__groep--' + o;
      const h = document.createElement('h4');
      h.className = 'samzwam-houtwijzer__groep-kop';
      h.appendChild(this.tekenEl(o));
      h.appendChild(document.createTextNode(' ' + this.s['groep_' + o]));
      sectie.appendChild(h);
      const lijst = document.createElement('ul');
      lijst.className = 'samzwam-houtwijzer__bomen';
      groep.forEach((c) => {
        const boom = this.bomen.find((b) => b.id === c.boom) || this.data.bomen.find((b) => b.id === c.boom);
        if (!boom) return;
        const li = document.createElement('li');
        const knop = document.createElement('button');
        knop.type = 'button';
        knop.className = 'samzwam-houtwijzer__boomlink link';
        knop.textContent = boom.naam[this.taal] || boom.naam.nl;
        // Doorklikken naar die boom: dan zie je meteen wat er nog meer op kan.
        knop.addEventListener('click', () => this.kiesBoom(boom.id));
        li.appendChild(knop);
        const b = this.bewijs(c);
        if (b) {
          const span = document.createElement('span');
          span.className = 'samzwam-houtwijzer__bewijs';
          span.textContent = ' ' + b;
          li.appendChild(span);
        }
        lijst.appendChild(li);
      });
      sectie.appendChild(lijst);
      paneel.appendChild(sectie);
    });

    paneel.appendChild(this.productRij(soort));
  }

  /* --- tabel ------------------------------------------------------------- */

  paneelTabel(paneel) {
    const hint = document.createElement('p');
    hint.className = 'samzwam-houtwijzer__scrollhint';
    hint.textContent = this.s.tabel_scroll;
    paneel.appendChild(hint);

    const wrap = document.createElement('div');
    wrap.className = 'samzwam-houtwijzer__tabelwrap';
    wrap.tabIndex = 0;   // toetsenbord kan dan horizontaal scrollen
    wrap.setAttribute('role', 'region');
    wrap.setAttribute('aria-label', this.s.tab_tabel);

    const tabel = document.createElement('table');
    tabel.className = 'samzwam-houtwijzer__tabel';
    const kop = tabel.createTHead().insertRow();
    const hoek = document.createElement('th');
    hoek.scope = 'col';
    hoek.textContent = this.s.tabel_soort;
    kop.appendChild(hoek);
    this.bomen.forEach((b) => {
      const th = document.createElement('th');
      th.scope = 'col';
      const knop = document.createElement('button');
      knop.type = 'button';
      knop.className = 'samzwam-houtwijzer__tabelboom';
      knop.textContent = b.naam[this.taal] || b.naam.nl;
      knop.addEventListener('click', () => this.kiesBoom(b.id));
      th.appendChild(knop);
      kop.appendChild(th);
    });

    const romp = tabel.createTBody();
    this.soorten.forEach((s) => {
      const rij = romp.insertRow();
      const th = document.createElement('th');
      th.scope = 'row';
      const knop = document.createElement('button');
      knop.type = 'button';
      knop.className = 'samzwam-houtwijzer__tabelsoort';
      knop.textContent = s.naam[this.taal] || s.naam.nl;
      knop.addEventListener('click', () => { this.weergave = 'soort'; this.soort = s.id; this.teken(); });
      th.appendChild(knop);
      rij.appendChild(th);
      const perBoom = {};
      (this.perSoort[s.id] || []).forEach((c) => { perBoom[c.boom] = c; });
      this.bomen.forEach((b) => {
        const td = rij.insertCell();
        const c = perBoom[b.id];
        if (c) {
          td.className = 'samzwam-houtwijzer__cel samzwam-houtwijzer__cel--' + c.o;
          td.appendChild(this.tekenEl(c.o));
        } else {
          const leeg = document.createElement('span');
          leeg.className = 'visually-hidden';
          leeg.textContent = this.s.leeg_teken;
          td.appendChild(leeg);
        }
      });
    });

    wrap.appendChild(tabel);
    paneel.appendChild(wrap);
  }

  /* ------------------------------------------------------------------------ */
  /* Onderdelen                                                                */
  /* ------------------------------------------------------------------------ */

  /** Een rij knoppen om uit te kiezen (bomen of soorten). */
  kiezer(label, opties, gekozen, opKies) {
    const fs = document.createElement('fieldset');
    fs.className = 'samzwam-houtwijzer__kiezer';
    const lg = document.createElement('legend');
    lg.className = 'samzwam-houtwijzer__kiezer-label';
    lg.textContent = label;
    fs.appendChild(lg);
    const rij = document.createElement('div');
    rij.className = 'samzwam-houtwijzer__chips';
    opties.forEach((o) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'samzwam-houtwijzer__chip';
      b.setAttribute('aria-pressed', o.id === gekozen ? 'true' : 'false');
      b.textContent = o.label;
      b.addEventListener('click', () => opKies(o.id));
      rij.appendChild(b);
    });
    fs.appendChild(rij);
    return fs;
  }

  /** Een productkaart in de boomweergave. */
  kaart(soort, cel, afgeraden) {
    const li = document.createElement('li');
    li.className = 'samzwam-houtwijzer__kaart' + (afgeraden ? ' samzwam-houtwijzer__kaart--nee' : '');

    const eerste = soort.prods[0];
    if (this.dataset.fotos === 'aan' && eerste.i && !afgeraden) {
      const img = document.createElement('img');
      img.className = 'samzwam-houtwijzer__foto';
      img.src = eerste.i;
      img.alt = '';
      img.width = 80;
      img.height = 80;
      img.loading = 'lazy';
      li.appendChild(img);
    }

    const tekst = document.createElement('div');
    tekst.className = 'samzwam-houtwijzer__kaart-tekst';

    const naam = document.createElement('p');
    naam.className = 'samzwam-houtwijzer__naam';
    naam.appendChild(this.tekenEl(cel.o));
    naam.appendChild(document.createTextNode(' ' + (soort.naam[this.taal] || soort.naam.nl)));
    tekst.appendChild(naam);

    const b = this.bewijs(cel);
    if (b) {
      const p = document.createElement('p');
      p.className = 'samzwam-houtwijzer__bewijs';
      p.textContent = b;
      tekst.appendChild(p);
    }

    // Een soortopmerking (eikhaas: veel geduld) hoort bij de aanbevelingen,
    // niet bij "niet op dit hout": daar is hij overbodig.
    if (soort.tip && !afgeraden) tekst.appendChild(this.tip(soort.tip[this.taal] || soort.tip.nl));

    if (!afgeraden) tekst.appendChild(this.productRij(soort));
    li.appendChild(tekst);
    return li;
  }

  /** Prijs en links naar de producten van een soort (soms twee: wit en geel). */
  productRij(soort) {
    const rij = document.createElement('div');
    rij.className = 'samzwam-houtwijzer__producten';
    soort.prods.forEach((p) => {
      const a = document.createElement('a');
      a.className = 'samzwam-houtwijzer__product';
      a.href = p.u;
      if (p.a) {
        const label = soort.prods.length > 1 ? p.t : this.s.bekijk;
        a.textContent = label + ' · ' + (p.v ? this.s.vanaf.replace('[prijs]', p.p) : p.p);
      } else {
        a.textContent = (soort.prods.length > 1 ? p.t + ' · ' : '') + this.s.uitverkocht;
        a.classList.add('samzwam-houtwijzer__product--uit');
      }
      rij.appendChild(a);
    });
    return rij;
  }

  tip(tekst) {
    const p = document.createElement('p');
    p.className = 'samzwam-houtwijzer__tip';
    p.textContent = tekst;
    return p;
  }

  /** Het teken met een tekst voor schermlezers. */
  tekenEl(o) {
    const span = document.createElement('span');
    span.className = 'samzwam-houtwijzer__teken samzwam-houtwijzer__teken--' + o;
    const zicht = document.createElement('span');
    zicht.setAttribute('aria-hidden', 'true');
    zicht.textContent = { u: '★', g: '●', m: '○', a: '✗' }[o];
    span.appendChild(zicht);
    const sr = document.createElement('span');
    sr.className = 'visually-hidden';
    sr.textContent = this.s['oordeel_' + o];
    span.appendChild(sr);
    return span;
  }

  /** Een korte regel die zegt waar het oordeel op rust. */
  bewijs(c) {
    const d = [];
    if (c.l) d.push(this.s.bewijs_l);
    if (c.vn) {
      d.push(this.s.bewijs_v
        .replace('[pct]', String(c.vp))
        .replace('[n]', c.vn.toLocaleString(this.taal === 'en' ? 'en-GB' : this.taal + '-' + this.taal.toUpperCase())));
    }
    if (c.p) d.push(this.s.bewijs_p);
    if (c.e && this.s.bewijs_e) d.push(this.s.bewijs_e);
    if (!d.length) return '';
    const zin = d.join(', ');
    return zin.charAt(0).toUpperCase() + zin.slice(1);
  }

  statusTekst() {
    if (this.weergave === 'boom' && this.boom) {
      const b = this.bomen.find((x) => x.id === this.boom);
      const n = (this.data.cellen[this.boom] || []).filter((c) => this.soortOp[c.s] && c.o !== 'a').length;
      return this.s.status_boom.replace('[n]', n).replace('[boom]', b ? (b.naam[this.taal] || b.naam.nl) : '');
    }
    if (this.weergave === 'soort' && this.soort) {
      const s = this.soortOp[this.soort];
      const n = (this.perSoort[this.soort] || []).filter((c) => c.o !== 'a').length;
      return this.s.status_soort.replace('[n]', n).replace('[soort]', s.naam[this.taal] || s.naam.nl);
    }
    return '';
  }

  /**
   * Een boom kiezen. Op een boompagina gaat een andere boom naar zijn eigen
   * pagina, zodat het adres altijd bij de inhoud past; elders wisselt de
   * houtwijzer gewoon binnen de pagina.
   */
  kiesBoom(id) {
    if (this.paginaBoom && id !== this.paginaBoom && this.paginas[id]) {
      location.href = new URL(this.paginas[id], location.href).toString();
      return;
    }
    this.weergave = 'boom';
    this.boom = id;
    this.teken();
  }

  /** Houd de adresbalk gelijk met de keuze, zonder de pagina te herladen. */
  werkAdresBij() {
    try {
      const u = new URL(location.href);
      ['boom', 'soort', 'weergave'].forEach((k) => u.searchParams.delete(k));
      if (this.weergave === 'boom' && this.boom && this.boom === this.paginaBoom) { /* het adres is de boom al */ }
      else if (this.weergave === 'boom' && this.boom) u.searchParams.set('boom', this.boom);
      else if (this.weergave === 'soort' && this.soort) u.searchParams.set('soort', this.soort);
      else if (this.weergave !== (this.dataset.start || 'boom')) u.searchParams.set('weergave', this.weergave);
      history.replaceState(history.state, '', u.toString());
    } catch (e) { /* oude browser: dan loopt de adresbalk niet mee, verder niets */ }
  }

  id(naam) {
    if (!this.uid) this.uid = 'hw-' + Math.random().toString(36).slice(2, 8);
    return this.uid + '-' + naam;
  }
}

if (!customElements.get('samzwam-houtwijzer')) {
  customElements.define('samzwam-houtwijzer', SamzwamHoutwijzer);
}
